import React from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import ExpandingTextArea from 'react-expanding-textarea';
import moment from 'moment';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import InstructorService from 'services/InstructorService';
import LanguageService from 'services/LanguageService';
import CourseService from 'services/CourseService';
import CustomerService from 'services/CustomerService';
import BookingService from 'services/BookingService';
import LocationService from 'services/LocationService';
import UserService from 'services/UserService';
import BootboxHelper from 'helpers/BootboxHelper';
import TextHelpers from 'helpers/TextHelpers';
import PartnerService from 'services/PartnerService';
import ContactService from 'services/ContactService';
import CostCentreService from 'services/CostCentreService';
import VendorService from 'services/VendorService';

// Components
import Loader from 'components/common/Loader';
import DatePicker from 'components/common/DatePicker';
import BookingInvoiceModal from 'components/invoicing/BookingInvoiceModal';
import EditJobRequestModal from 'components/admin/job-request/EditJobRequestModal';
import CSATResponseModal from 'components/bookings/CSATResponseModal';
import NotesTable from 'components/notes/NotesTable';

//-------------------------------------------------------------------------------------------------------------------

class EditBookingModal extends React.Component {

    constructor(props) {
        super(props);

        this.editJobRequestModalRef = React.createRef();
        this.bookingInvoiceModalRef = React.createRef();
        this.csatResponseModalRef = React.createRef();

        this.state = {
            isOpen: false
        };
    }

    show(opts) {
        return new Promise((resolve, reject) => {
            this.opts = opts;
            this.bookingID = opts.bookingID;
            this.resolve = resolve;
            this.loginInfo = GlobalStateService.getValue('loginInfo');

            this.setState({
                isOpen: true,
                isLoading: true,
                isReadOnly: !this.loginInfo.isAdmin,
                instructors: [],
                costCentres: []
            });
            this.load();
        });
    }

    close() {
        this.setState({
            isOpen: false
        });
    }

    async load() {
        let bookingReq = null;
        if (this.bookingID) {
            bookingReq = BookingService.get(this.bookingID);
        }
        const citiesReq = LocationService.listCities();
        const languagesReq = LanguageService.list();
        const vendorUsersReq = UserService.list({
            role: 'Vendor',
            canMakeEnquiries: true
        });
        const vendorsReq = VendorService.list();
        const customersReq = CustomerService.list();
        const partnersReq = PartnerService.list();
        const geosReq = LocationService.listGeos();

        // Get loaded items
        const geos = await geosReq;
        const languages = await languagesReq;
        const vendors = await vendorsReq;
        const vendorUsers = await vendorUsersReq;
        const customers = await customersReq;
        const cities = await citiesReq;
        const partners = await partnersReq;

        // Set defaults
        let languageID = null;
        const english = languages.find(l => l.name == 'English');
        if (english) {
            languageID = english.id;
        }
        let geoID = this.opts.geoID;
        if (!geoID) {
            const defaultGeo = geos.find(g => g.isDefault);
            if (defaultGeo) {
                geoID = defaultGeo.id;
            } else if (geos.length > 0) {
                geoID = geos[0].id;
            }
        }
        let vendorID = this.opts.vendorID;
        if (!vendorID && vendors.length > 0) {
            const defaultVendor = vendors.find(v => v.isDefault);
            if (defaultVendor) {
                vendorID = defaultVendor.id;
            }
        }

        // Load or create booking
        let booking;
        if (this.bookingID) {
            booking = await bookingReq;
            if (this.opts.status) {
                booking.status = this.opts.status;
            }
            if (this.opts.dateCancelled) {
                booking.dateCancelled = this.opts.dateCancelled;
            }
            if (this.opts.importedBookingID) {
                booking.importedBookingID = this.opts.importedBookingID;
            }
        } else {
            booking = {
                ... this.opts,
                languageID,
                geoID,
                numDays: 1,
                modality: 'vILT',
                customers: [],
                dates: [],
                dateRuns: [],
                vendorID
            };
        }
        booking.vendor = vendors.find(v => v.id == booking.vendorID);
        booking.dateRuns.push({ isNew: true });

        this.setState({
            booking,
            languages,
            geos,
            vendors,
            vendorUsers,
            customers,
            cities,
            partners
        }, () => {
            this.loadVendorOrGeoSpecific();
        });
    }

    async loadVendorOrGeoSpecific() {
        const booking = { ...this.state.booking };
        const { vendorID, geoID } = booking;
        const courses = await CourseService.list({ vendorID });
        const costCentres = await CostCentreService.list({ vendorID, geoID, forceID: booking.costCentreID });
        const contacts = await ContactService.list({ vendorID, geoID, isOperationsOwner: true });
        const instructors = await InstructorService.list({
            vendorID,
            type: 'Approved',
            ignoreDeactivated: true
        });

        // Default cost centre
        if (!booking.id) {
            if (booking.costCentreID) {
                const costCentre = costCentres.find(cc => cc.id == booking.costCentreID);
                if (!costCentre) {
                    booking.costCentreID = null;
                }
            }
            if (!booking.costCentreID) {
                const defaultCostCentre = costCentres.find(cc => cc.isDefault && cc.vendorID == booking.vendorID && cc.geoID == booking.geoID);
                if (defaultCostCentre) {
                    booking.costCentreID = defaultCostCentre.id;
                }
            }
        }

        this.setState({
            booking,
            courses,
            contacts,
            costCentres,
            instructors,
            isLoading: false
        });
    }

    updateFields(values) {
        let loadVendorOrGeoSpecific = false;
        let checkCourseTier = false;
        const booking = { ...this.state.booking };

        for (var field in values) {
            let value = values[field];
            booking[field] = value;

            if (field == 'courseID' || field == 'instructorID') {
                checkCourseTier = true;
            }

            if (field == 'courseID' && !booking.isSplit) {
                // Get default number of days
                const course = this.state.courses.find(c => c.id == value);
                if (course) {
                    booking.numDays = course.defaultNumDays || 1;
                    if (booking.numDays && booking.isWithJAM) {
                        booking.numDays++;
                    }
                }
            }
            else if (field == 'isWithJAM' && !booking.isSplit) {
                booking.numDays += (value ? 1 : -1);
            }
            else if (field == 'status' && value == 'Cancelled' && !booking.dateCancelled) {
                booking.dateCancelled = moment().startOf('day').toDate();
            }
            else if (field == 'isSplit' && value && booking.dateRuns.length <= 1 && booking.startDate) {
                booking.dateRuns[0] = {
                    start: booking.startDate
                };
                booking.dateRuns.push({
                    isNew: true
                });
            }
            else if (field == 'csatScore') {
                if (value > 5) {
                    booking.csatScore = 5;
                } else if (value < 0) {
                    booking.csatScore = 0;
                }
            }
            else if (field == 'isCSATScoreNA' && value) {
                booking.csatScore = null;
            }
            else if (field == 'vendorID') {
                loadVendorOrGeoSpecific = true;
                booking.courseID = null;
                booking.costCentreID = null;
                booking.contactID = null;
                if (value) {
                    booking.vendor = this.state.vendors.find(v => v.id == booking.vendorID);
                    if (booking.vendor.name != 'AWS') {
                        booking.isWithJAM = false;
                    }
                } else {
                    booking.vendor = null;
                }
            } else if (field == 'geoID') {
                loadVendorOrGeoSpecific = true;
            }
        }
        this.setState({
            booking
        }, () => {
            if (loadVendorOrGeoSpecific) {
                this.loadVendorOrGeoSpecific();
            }

            // Automatically select tier based on certs
            if (checkCourseTier && booking.courseID && booking.instructorID) {
                const course = this.state.courses.find(c => c.id == booking.courseID);
                if (course && course.selectTierBasedOnCerts) {
                    this.autoSelectTierBasedOnCerts();
                }
            }
        });
    }

    async autoSelectTierBasedOnCerts() {
        const { booking } = this.state;
        const tier = await CourseService.getHighestTierForInstructor(booking.courseID, booking.instructorID);
        this.updateFields({ tier });
    }

    updateDateRunFields(index, values) {
        const dateRuns = [ ...this.state.booking.dateRuns ];
        const dateRun = { ...dateRuns[index] };
        for (var field in values) {
            let value = values[field];
            dateRun[field] = value;
        }
        if (dateRun.isNew) {
            delete dateRun.isNew;
            dateRuns.push({ isNew: true });
        }
        if (!dateRun.start && !dateRun.end) {
            dateRuns.splice(index, 1);
        } else {
            dateRuns[index] = dateRun;
        }
        this.updateFields({ dateRuns });
    }

    async save() {
        const { booking } = this.state;
        return new Promise(async (resolve) => {
            this.setState({ isLoading: true });
            try {
                const id = await BookingService.save(booking);
                if (!booking.id) {
                    this.updateFields({ id });
                }
                this.setState({
                    isLoading: false
                }, () => {
                    resolve(id);
                });
            } catch (error) {
                BootboxHelper.alert(error);
                this.setState({ isLoading: false });
            }
        });
    }

    async saveAndClose() {
        await this.save();
        this.close();
        this.resolve();
    }

    async viewInvoice() {
        this.bookingInvoiceModalRef.current.show({
            bookingID: this.state.booking.id
        });
    }

    async confirmIgnoreInvoice() {
        const confirm = await BootboxHelper.confirm('This will hide the booking from the instructor\'s pending invoice list. Are you sure?');
        if (confirm) {
            this.setState({ isLoading: true });
            await BookingService.ignoreInvoice(this.state.booking.id);
            this.updateFields({ ignoreInvoice: true });
            this.setState({ isLoading: false });
        }
    }

    async confirmDelete() {
        const isEnquiry = (this.state.booking.status == 'Enquiry');
        const confirm = await BootboxHelper.confirm(`Are you sure you want to delete this ${isEnquiry ? 'enquiry' : 'booking'}?`);
        if (confirm) {
            this.delete();
        }
    }

    async delete() {
        this.setState({
            isLoading: true
        });
        await BookingService.delete(this.state.booking.id);
        this.close();
        this.resolve();
    }

    async confirmArchive() {
        const isEnquiry = (this.state.booking.status == 'Enquiry');
        const confirm = await BootboxHelper.confirm(`Are you sure you want to archive this ${isEnquiry ? 'enquiry' : 'booking'}?`);
        if (confirm) {
            await this.save();
            this.archive();
        }
    }

    async archive() {
        this.setState({ isLoading: true });
        await BookingService.archive(this.state.booking.id);
        this.close();
        this.resolve();
    }

    swap() {
        const {
            booking
        } = this.state;

        this.editJobRequestModalRef.current.show({
            type: 'SwapSearch',
            bookingID: booking.id,
            courseID: booking.courseID,
            isWithJAM: booking.isWithJAM,
            languageID: booking.languageID,
            startDate: booking.startDate,
            numDays: booking.numDays,
            isSplit: booking.isSplit,
            dates: [...booking.dates],
            dateRuns: [...booking.dateRuns.map(dr => ({...dr}))]
        });
    }

    async saveAndPreviewPO() {
        await this.save();
        const url = `/api/partner/get-po/` + this.state.booking.id;
        window.open(url);
    }

    async saveAndSendPO() {
        await this.save();
        this.setState({ isLoading: true });
        await PartnerService.sendPO(this.state.booking.id);
        BootboxHelper.alert('PO Sent');
        this.load();
    }

    async showCSATResponseModal() {
        this.csatResponseModalRef.current.show({
            id: this.bookingID
        });
    }

    render() {
        const {
            isOpen
        } = this.state;

        if (!isOpen) {
            return null;
        }

        return (<>

            <Modal show className="edit-booking-modal">
                {this.renderInner()}
            </Modal>

            <BookingInvoiceModal ref={this.bookingInvoiceModalRef} />
            <EditJobRequestModal ref={this.editJobRequestModalRef} />
            <CSATResponseModal ref={this.csatResponseModalRef} />

        </>);
    }

    renderInner() {
        const {
            isLoading,
            booking,
            languages,
            courses,
            cities,
            instructors,
            vendorUsers,
            customers,
            partners,
            geos,
            contacts,
            costCentres,
            vendors,
            isReadOnly
        } = this.state;

        if (isLoading) {
            return (
                <Modal.Body>
                    <Loader />
                </Modal.Body>
            );
        }

        if (!booking.cityID && booking.location) {
            cities.push({
                id: 0,
                name: booking.location
            });
        }
        let city = null;
        if (booking.cityID) {
            city = cities.find(c => c.id == booking.cityID);
        } else if (booking.location) {
            city = cities.find(c => c.id == 0 && c.name == booking.location);
        }
        
        // Determine if we are past the end date
        let endDate;
        if (booking.isSplit) {
            if (booking.dateRuns.length > 0) {
                booking.dateRuns.forEach(dr => {
                    if (!endDate || (dr.end && moment(dr.end).isAfter(moment(endDate)))) {
                        endDate = dr.end;
                    }
                });
            }
        } else if (booking.startDate) {
            endDate = moment(booking.startDate).add(Math.ceil(booking.numDays) - 1, 'days');
        }
        const isPastEndDate = (endDate && moment().isAfter(endDate));

        // Get course info
        let course = null;
        if (booking.courseID) {
            course = this.state.courses.find(c => c.id == booking.courseID);
        }

        return (

            <form onSubmit={e => { e.preventDefault(); this.saveAndClose() }} className={isReadOnly ? ' read-only-form' : ''}>
                <Modal.Body>

                    <Row>

                        <Col md="3">

                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <select
                                    className="form-control"
                                    value={booking.status || ''}
                                    onChange={e => this.updateFields({ status: e.target.value })}
                                    readOnly={isReadOnly}
                                >
                                    <option value="Enquiry">Enquiry</option>
                                    <option value="Hold">Hold</option> 
                                    <option value="Tentative">Tentative</option>
                                    <option value="Active">Active</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>
                            </Form.Group>

                        </Col>

                        <Col md="9">

                            {booking.status == 'Cancelled' &&

                                <Row>

                                    <Col md="6">

                                        <Form.Group className="mb-3">
                                            <Form.Label>Date cancelled</Form.Label>
                                            <DatePicker
                                                className="form-control"
                                                selected={booking.dateCancelled ? new Date(booking.dateCancelled) : null}
                                                onChange={value => this.updateFields({ dateCancelled: value })}
                                                readOnly={isReadOnly}
                                            />
                                        </Form.Group>

                                    </Col>

                                    <Col md="6">

                                        <Form.Group className="mb-3">
                                            <Form.Label>Invoice %</Form.Label>
                                            <select
                                                className="form-control"
                                                value={booking.cancelledInvoiceProportion || ''}
                                                onChange={e => this.updateFields({ cancelledInvoiceProportion: e.target.value })}
                                            >
                                                <option value="0">0%</option>
                                                <option value="0.5">50%</option>
                                                <option value="1">100%</option>
                                            </select>
                                        </Form.Group>

                                    </Col>

                                </Row>
                            }

                            {booking.status == 'Enquiry' &&
                                <Form.Group className="mb-3">
                                    <Form.Label>Unique Reference</Form.Label>
                                    <input
                                        type="text"
                                        readOnly
                                        className="form-control"
                                        defaultValue={booking.uniqueRef || '(Generated on save)'}
                                    />
                                </Form.Group>
                            }

                            <Row>
                                <Col md="2">
                                    <Form.Group className="mb-3">
                                        <Form.Label>ATP</Form.Label>
                                        <Form.Check
                                            type="checkbox"
                                            className="large-checkbox"
                                            checked={booking.isATPBooking || false}
                                            onChange={e => this.updateFields({ isATPBooking: e.target.checked })}
                                            id="is-atp-booking"
                                            label="ATP"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md="7">
                                    {booking.isATPBooking &&
                                        <Form.Group className="mb-3">
                                            <Form.Label>Partner</Form.Label>
                                            <Select
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                value={booking.partnerID ? partners.find(p => p.id == booking.partnerID) : null}
                                                options={partners}
                                                getOptionValue={p => p.id}
                                                getOptionLabel={p => p.name}
                                                onChange={v => this.updateFields({ partnerID: v.id })}
                                                isDisabled={isReadOnly}
                                            />
                                        </Form.Group>
                                    }
                                </Col>

                                {(booking.status == 'Active' || booking.status == 'Completed') && isPastEndDate &&
                                    <Col md="3">
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="dummy-control">
                                                <div className="float-right">
                                                    <input
                                                        type="checkbox"
                                                        checked={booking.isCSATScoreNA}
                                                        onChange={e => this.updateFields({ isCSATScoreNA: e.target.checked })}
                                                    /> N/A
                                                </div>
                                                CSAT
                                                {booking.hasCSATResponse && <>
                                                    {' '}
                                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => this.showCSATResponseModal()}>
                                                        <span className="fa fa-search" />
                                                    </button>
                                                </>}
                                            </Form.Label>
                                            <input
                                                className="form-control"
                                                type="number"
                                                min={0}
                                                max={5}
                                                step={0.01}
                                                value={(Math.round((booking.csatScore || 0) * 100) / 100) || ''}
                                                onChange={e => this.updateFields({ csatScore: e.target.value })}
                                                disabled={!!booking.isCSATScoreNA}
                                            />
                                        </Form.Group>
                                    </Col>
                                }
                            </Row>

                        </Col>

                    </Row>

                    {booking.status == 'Enquiry' && <>
                        
                        <Row>

                            <Col md="4">

                                <Form.Group className="mb-3">
                                    <Form.Label>Date of enquiry</Form.Label>
                                    <DatePicker
                                        className="form-control"
                                        selected={booking.enquiryDate ? new Date(booking.enquiryDate) : null}
                                        onChange={value => this.updateFields({ enquiryDate: value })}
                                        readOnly={isReadOnly}
                                    />
                                </Form.Group>

                            </Col>

                            <Col md="4">

                                <Form.Group className="mb-3">
                                    <Form.Label>Requested by</Form.Label>
                                    <select
                                        className="form-control"
                                        value={booking.enquiryUserID || ''}
                                        onChange={e => this.updateFields({ enquiryUserID: e.target.value })}
                                        disabled={isReadOnly}
                                    >
                                        <option value="">(Please select)</option>
                                        {vendorUsers.map(u =>
                                            <option key={u.id} value={u.id}>
                                                {u.firstName} {u.lastName}
                                            </option>
                                        )}
                                    </select>
                                </Form.Group>

                            </Col>

                            <Col md="4">

                                <Form.Group className="mb-3">
                                    <Form.Label>Source</Form.Label>
                                    <select
                                        className="form-control"
                                        value={booking.enquirySource || ''}
                                        onChange={e => this.updateFields({ enquirySource: e.target.value })}
                                        disabled={isReadOnly}
                                    >
                                        <option value="">(Please select)</option> 
                                        <option value="Portal">Portal</option> 
                                        <option value="Chime">Chime</option> 
                                        <option value="Slack">Slack</option> 
                                        <option value="Email">Email</option> 
                                        <option value="Phone">Phone</option> 
                                    </select>
                                </Form.Group>
                                
                            </Col>

                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Enquiry details</Form.Label>
                            <ExpandingTextArea
                                rows={3}
                                className="form-control"
                                value={booking.enquiryDetails || ''}
                                onChange={e => this.updateFields({ enquiryDetails: e.target.value })}
                                readOnly={isReadOnly}
                            />
                        </Form.Group>

                        <hr />

                    </>}

                    <Row>

                        <Col md="2">
                            <Form.Group className="mb-3">
                                <Form.Label>Vendor</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={booking.vendorID ? vendors.find(v => v.id == booking.vendorID) : null}
                                    options={vendors}
                                    getOptionValue={v => v.id}
                                    getOptionLabel={v => v.name}
                                    onChange={v => this.updateFields({ vendorID: v.id })}
                                    isDisabled={isReadOnly}
                                />
                            </Form.Group>
                        </Col>

                        <Col md="8">

                            <Form.Group className="mb-3">
                                <Form.Label>Course</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={booking.courseID ? courses.find(c => c.id == booking.courseID) : null}
                                    options={booking.id ? courses : courses.filter(c => !c.isDeprecated)}
                                    getOptionValue={c => c.id}
                                    getOptionLabel={c => c.name + (c.tier ? ` (${c.tier})` : '')}
                                    onChange={v => this.updateFields({ courseID: v.id })}
                                    isDisabled={isReadOnly}
                                />
                            </Form.Group>

                        </Col>

                        <Col md="2">

                            <Form.Group className="mb-3">
                                <Form.Label>Tier</Form.Label>
                                {course && course.tier ?
                                    <p className="form-control-plaintext">{course.tier}</p> :
                                    <select
                                        className="form-control"
                                        value={booking.tier || ''}
                                        onChange={e => this.updateFields({ tier: e.target.value })}
                                        readOnly={isReadOnly}
                                    >
                                        <option value="">(Select)</option>
                                        <option value="Associate">Associate</option>
                                        <option value="Professional">Professional</option>
                                        <option value="Specialty">Specialty</option>
                                    </select>
                                }
                            </Form.Group>
                        </Col>

                    </Row>

                    <Row>
                        {booking.vendor && booking.vendor.name == 'AWS' &&
                            <Col md="2">
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        checked={booking.isWithJAM}
                                        onChange={e => this.updateFields({ isWithJAM: e.target.checked })}
                                        id="is-with-jam"
                                        label="With AWS Jam"
                                        disabled={isReadOnly}
                                    />
                                </Form.Group>
                            </Col>
                        }
                        <Col md="2">
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    checked={booking.isShadow}
                                    onChange={e => this.updateFields({ isShadow: e.target.checked })}
                                    id="is-shadow"
                                    label="Shadow class"
                                    disabled={isReadOnly}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Geo</Form.Label>
                                <select
                                    className="form-control"
                                    value={booking.geoID || ''}
                                    onChange={e => this.updateFields({ geoID: parseInt(e.target.value) })}
                                    readOnly={isReadOnly}
                                >
                                    {geos.map(g =>
                                        <option key={g.id} value={g.id}>
                                            {g.name}
                                        </option>
                                    )}
                                </select>
                            </Form.Group>

                        </Col>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Instructor</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={booking.instructorID ? instructors.find(i => i.id == booking.instructorID) : null}
                                    options={instructors}
                                    getOptionValue={i => i.id}
                                    getOptionLabel={i => TextHelpers.formatInstructorName(i, true)}
                                    onChange={v => this.updateFields({ instructorID: v.id })}
                                    isDisabled={isReadOnly}
                                />
                            </Form.Group>

                        </Col>

                    </Row>

                    <Row>

                        {!booking.isATPBooking &&
                            <Col md="6">

                                <Form.Group className="mb-3">
                                    <Form.Label>Customer(s)</Form.Label>
                                    <Creatable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isClearable
                                        isMulti
                                        isDisabled={isReadOnly}
                                        options={customers}
                                        value={booking.customers}
                                        getOptionValue={b => b.id}
                                        getOptionLabel={b => b.__isNew__ ? b.label : b.name}
                                        formatCreateLabel={name => <>Add <b>{name}</b></>}
                                        onChange={customers => {
                                            customers.forEach(c => {
                                                if (c.__isNew__) {
                                                    c.name = c.label;
                                                    //this.addCustomer(c);
                                                }
                                            });
                                            this.updateFields({ customers });
                                        }}
                                        readOnly={isReadOnly}
                                    />
                                </Form.Group>

                            </Col>
                        }

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Language</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={booking.languageID ? languages.find(l => l.id == booking.languageID) : null}
                                    options={languages}
                                    getOptionValue={l => l.id}
                                    getOptionLabel={l => l.name}
                                    onChange={v => this.updateFields({ languageID: v.id })}
                                    isDisabled={isReadOnly}
                                />
                            </Form.Group>

                        </Col>


                    </Row>

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Dates</Form.Label>
                                <Form.Check
                                    checked={booking.isSplit || false}
                                    onChange={e => this.updateFields({ isSplit: e.target.checked })}
                                    disabled={isReadOnly}
                                    id="is-split"
                                    label="Split booking dates"
                                />
                                {booking.isSplit && this.renderDatesTable()}
                                <Form.Check
                                    type="checkbox"
                                    checked={booking.isHalfDays || booking.numDays == 0.5}
                                    onChange={e => this.updateFields({ isHalfDays: e.target.checked })}
                                    disabled={isReadOnly || booking.numDays == 0.5}
                                    id="is-half-days"
                                    label="Billed at half-day rate"
                                />
                            </Form.Group>

                        </Col>

                        <Col md="3">

                            {!booking.isSplit &&
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker
                                        className="form-control"
                                        selected={booking.startDate ? new Date(booking.startDate) : null}
                                        onChange={value => this.updateFields({ startDate: value })}
                                        readOnly={isReadOnly}
                                    />
                                </Form.Group>
                            }

                        </Col>

                        <Col md="3">

                            {!booking.isSplit &&
                                <Form.Group className="mb-3">
                                    <Form.Label>Duration</Form.Label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        step="any"
                                        min="0"
                                        value={booking.numDays || ''}
                                        onChange={e => this.updateFields({ numDays: e.target.value })}
                                        readOnly={isReadOnly}
                                    />
                                </Form.Group>
                            }

                        </Col>

                    </Row>

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Location</Form.Label>
                                <Creatable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                    isDisabled={isReadOnly}
                                    readOnly={isReadOnly}
                                    options={cities}
                                    value={city}
                                    getOptionValue={b => b.id}
                                    getOptionLabel={b => b.__isNew__ ? b.label : b.name}
                                    formatCreateLabel={name => <>Add <b>{name}</b></>}
                                    onChange={city => {
                                        if (!city) {
                                            this.updateFields({
                                                cityID: null,
                                                location: null
                                            });
                                        } else if (city.__isNew__ || city.id == 0) {
                                            this.updateFields({
                                                cityID: null,
                                                location: city.label
                                            });
                                        } else {
                                            this.updateFields({
                                                cityID: city.id,
                                                location: null
                                            });
                                        }
                                    }}
                                />
                            </Form.Group>

                        </Col>

                        <Col md="6">
                             <Form.Group className="mb-3">
                                <Form.Label>{booking.isATPBooking ? 'Booking Reference' : 'LMS ID'}</Form.Label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={booking.lmsid || ''}
                                    onChange={e => this.updateFields({ lmsid: e.target.value })}
                                    readOnly={isReadOnly}
                                    placeholder={booking.isATPBooking ? 'Generated automatically' : ''}
                                />
                            </Form.Group>
                        </Col>

                    </Row>

                    <Row>

                        <Col md={booking.modality == 'ILT' ? 3 : 6}>

                            <Form.Group className="mb-3">
                                <Form.Label>Modality</Form.Label>
                                <select
                                    className="form-control"
                                    value={booking.modality || ''}
                                    onChange={e => this.updateFields({ modality: e.target.value })}
                                    readOnly={isReadOnly}
                                >
                                    <option value="">(Select)</option>
                                    <option value="vILT">vILT</option>
                                    <option value="ILT">ILT</option>
                                </select>
                            </Form.Group>

                        </Col>

                        {booking.modality == 'ILT' &&
                            <Col md="3">
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>&nbsp;</Form.Label>
                                    <Form.Check
                                        type="checkbox"
                                        className="large-checkbox"
                                        checked={booking.hasTravelExpenses || false}
                                        onChange={e => this.updateFields({ hasTravelExpenses: e.target.checked })}
                                        id="has-travel-expenses"
                                        label="Travel expenses"
                                    />
                                </Form.Group>

                            </Col>
                        }

                        <Col md="6">

                            {booking.vendor &&
                                <Form.Group className="mb-3">
                                    <Form.Label>TOpS Contact</Form.Label>
                                    <select
                                        className="form-control"
                                        value={booking.contactID || ''}
                                        onChange={e => this.updateFields({ contactID: parseInt(e.target.value) })}
                                        readOnly={isReadOnly}
                                    >
                                        <option value="">(Select)</option>
                                        {contacts.map(c =>
                                            <option key={c.id} value={c.id}>
                                                {c.firstName || ''} {c.lastName || ''}
                                            </option>
                                        )}
                                    </select>
                                </Form.Group>
                            }

                        </Col>

                    </Row>

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Cost centre</Form.Label>
                                <select
                                    className="form-control"
                                    value={booking.costCentreID || ''}
                                    onChange={e => this.updateFields({ costCentreID: parseInt(e.target.value) })}
                                    readOnly={isReadOnly}
                                >
                                    <option value="">(Select)</option>
                                    {costCentres.map(cc =>
                                        <option key={cc.id} value={cc.id}>
                                            {cc.name} ({cc.poNumber})
                                        </option>
                                    )}
                                </select>
                            </Form.Group>

                        </Col>

                    </Row>

                    {booking.isATPBooking && booking.status == 'Active' && <>
                        <hr />
                        
                        <div className="row">
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Purchase Order</Form.Label>
                                    <p className="form-control-plaintext">
                                        {booking.dateATPPOSent ? 'Sent ' + moment(booking.dateATPPOSent).format('DD/MM/YYYY') : 'Not sent yet'}
                                    </p>
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>ATP Fee</Form.Label>
                                    <input
                                        className="form-control"
                                        type="number"
                                        step="0.01"
                                        value={booking.atpFee || ''}
                                        onChange={e => this.updateFields({ atpFee: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4 btn-stack">
                                <button type="button" className="btn btn-secondary w-100 mb-1" onClick={() => this.saveAndSendPO()}>
                                    Send PO
                                </button>

                                <button type="button" className="btn btn-secondary w-100" onClick={() => this.saveAndPreviewPO()}>
                                    Preview PO
                                </button>
                            </div>
                        </div>
                    </>}

                    <Form.Group className="mb-3">
                        <Form.Label>Internal Notes</Form.Label>
                        {!!booking.id ?
                            <NotesTable
                                bookingID={booking.id}
                                showNewestFirst
                                allowShowAll
                                maxNum={5}
                                noteTypes={['General']}
                                newNoteType="General"
                                isReadOnly={isReadOnly}
                            /> :
                            <ExpandingTextArea
                                className="form-control"
                                rows={3}
                                value={booking.newNotes || ''}
                                onChange={e => this.updateFields({ newNotes: e.target.value })}
                                readOnly={isReadOnly}
                            />
                        }
                    </Form.Group>
                    {/*
                    {(!isReadOnly || booking.notes) &&
                        <Form.Group className="mb-3">
                            <Form.Label>Internal Notes</Form.Label>
                            <ExpandingTextArea
                                className="form-control"
                                rows={3}
                                value={booking.notes || ''}
                                onChange={e => this.updateFields({ notes: e.target.value })}
                                readOnly={isReadOnly}
                            />
                        </Form.Group>
                    }
                    */}
                    
                    {!!booking.importedBookingID &&
                        <Form.Group className="mb-3">
                            <Form.Label>Email Notes</Form.Label>
                            <ExpandingTextArea
                                className="form-control"
                                rows={3}
                                value={booking.emailNotes || ''}
                                onChange={e => this.updateFields({ emailNotes: e.target.value })}
                                readOnly={isReadOnly}
                            />
                        </Form.Group>
                    }

                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary me-auto" onClick={() => this.close()}>
                        Cancel changes
                    </button>
                    {this.loginInfo.isAdmin && !isReadOnly && !!booking.id &&
                        <button type="button" className="btn btn-danger ms-auto me-auto" onClick={() => this.confirmDelete()}>
                            Delete
                        </button>
                    }
                    {this.loginInfo.isAdmin && !isReadOnly && !!booking.id && (booking.status == 'Enquiry' || booking.status == 'Hold') &&
                        <button type="button" className="btn btn-secondary ms-auto me-auto" onClick={() => this.confirmArchive()}>
                            Archive
                        </button>
                    }
                    {this.loginInfo.isAdmin && !isReadOnly && !!booking.id &&
                        <button type="button" className="btn btn-secondary ms-auto me-auto" onClick={() => this.swap()}>
                            Swap
                        </button>
                    }
                    {this.loginInfo.isAdmin && !!booking.invoiceID &&
                        <button type="button" className="btn btn-secondary ms-auto me-auto" onClick={() => this.viewInvoice()}>
                            View invoice
                        </button>
                    }
                    {this.loginInfo.isAccountantOrAdmin && !!booking.id && !booking.invoiceID && !booking.ignoreInvoice && !booking.isShadow &&
                        <button type="button" className="btn btn-secondary ms-auto me-auto" onClick={() => this.confirmIgnoreInvoice()}>
                            Skip invoicing
                        </button>
                    }
                    {!isReadOnly &&
                        <button type="submit" className="btn btn-primary ms-auto">
                            Save & close
                        </button>
                    }
                </Modal.Footer>
            </form>
        );
    }

    renderDatesTable() {
        const {
            booking,
            isReadOnly
        } = this.state;

        const dateRuns = [...booking.dateRuns];
        if (!dateRuns.find(dr => dr.isNew)) {
            dateRuns.push({
                isNew: true
            });
        }
        
        return (
            <table className="table table-bordered dates-table mt-3">
                <thead>
                    <tr>
                        <th>From</th>
                        <th>To</th>
                    </tr>
                </thead>
                <tbody>
                    {dateRuns.map((dr, index) =>
                        <tr key={index}>
                            <td>
                                <DatePicker
                                    className="form-control"
                                    placeholder="Select..."
                                    selected={dr.start ? new Date(dr.start) : null}
                                    onChange={value => this.updateDateRunFields(index, { start: value })}
                                    minDate={index > 0 && booking.dateRuns[index - 1].end ? new Date(booking.dateRuns[index - 1].end) : null}
                                    readOnly={isReadOnly}
                                />
                            </td>
                            <td>
                                {dr.start &&
                                    <DatePicker
                                        className="form-control"
                                        selected={dr.end ? new Date(dr.end) : null}
                                        onChange={value => this.updateDateRunFields(index, { end: value })}
                                        minDate={dr.start ? new Date(dr.start) : null}
                                        readOnly={isReadOnly}
                                    />
                                }
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        );
    }
}

export default EditBookingModal;